import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import { APIHOST } from '../../common';
import { useAuth, getToken } from '../auth/AuthContext';

interface PaymentCardInfo {
  NameOnCard?: string;
  Type?: string;
  Number?: string;
  ExpirationDate?: {
    Month?: string;
    Year?: string;
  };
  CVV?: string;
  ZipCode?: string;
}

interface CreditCardInfoResponse {
  data?: PaymentCardInfo;
}

interface DisplayCreditCardProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

export default function DisplayCreditCard({ open, onClose, orderId }: DisplayCreditCardProps) {
  const { currentUser } = useAuth();
  const [paymentCard, setPaymentCard] = useState<PaymentCardInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalTransactionId, setExternalTransactionId] = useState('');
  const [internalTransactionId, setInternalTransactionId] = useState('');
  const [transactionIdMessage, setTransactionIdMessage] = useState<string | null>(null);
  const [savingTransactionIds, setSavingTransactionIds] = useState(false);

  const roleType = currentUser?.role?.name || '';
  const showInternalTransactionId = roleType.toLowerCase() === 'internal';

  useEffect(() => {
   
      if (!orderId) return;
      if (!open) return;

      loadPaymentCardInfo();
      console.log('Loading payment card info for order:', orderId);
  }, [orderId, open]);

  const loadPaymentCardInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      setPaymentCard(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const paymentCardResponse = await fetch(`${APIHOST}/api/orders/${orderId}/paymentCard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!paymentCardResponse.ok) {
        throw new Error('Failed to fetch payment card info');
      }

      const cardPayload = await paymentCardResponse.json() as CreditCardInfoResponse;
      setPaymentCard(cardPayload?.data ?? null);
    } catch (err) {
      console.error('Error loading payment card info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payment card info');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentCard(null);
    setError(null);
    setTransactionIdMessage(null);
    onClose();
  };

  const handleTransactionIdSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTransactionIdMessage(null);

    const token = getToken();
    if (!token) {
      setError('No authentication token found');
      return;
    }

    try {
      setSavingTransactionIds(true);
      setError(null);

      const isInternalUser = currentUser?.role?.name === 'Internal';
      const endpoint = isInternalUser
        ? `${APIHOST}/api/orders/${orderId}/processPaymentCard`
        : `${APIHOST}/api/hotel-admin/orders/${orderId}/processPaymentCard`;
      const payload = isInternalUser
        ? { internalTransactionId, externalTransactionId }
        : { externalTransactionId };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save transaction ID values');
      }

      setTransactionIdMessage('Transaction ID values saved successfully.');
    } catch (err) {
      console.error('Error saving transaction IDs:', err);
      setError(err instanceof Error ? err.message : 'Failed to save transaction ID values');
    } finally {
      setSavingTransactionIds(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px'}
      }}
    >
      <DialogTitle>Credit Card Information</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {paymentCard && !loading && (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small" aria-label="credit card details">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600, width: '35%' }}>Name On Card</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>{paymentCard.NameOnCard || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>Card Number</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>{paymentCard.Number || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>Card Type</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>{paymentCard.Type || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>Expiration</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {paymentCard.ExpirationDate?.Month || 'N/A'}/{paymentCard.ExpirationDate?.Year || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>CVV</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>{paymentCard.CVV || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>Zip</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>{paymentCard.ZipCode || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box p={2} component="form" onSubmit={handleTransactionIdSubmit}>
          <Stack spacing={2}>
            {showInternalTransactionId && (
              <TextField
                label="Internal Transaction ID"
                value={internalTransactionId}
                onChange={(event) => setInternalTransactionId(event.target.value)}
                fullWidth
                helperText="For internal use only. Visible to CWHS employees. Not shared with hotels."
              />
            )}
            {/* <TextField
              label="Hotel's Confirmation"
              value={externalTransactionId}
              onChange={(event) => setExternalTransactionId(event.target.value)}
              fullWidth
              helperText="This is the hotel's confirmation ID for processing this card. This allows hotels to track their own confirmation IDs in relation to this order."
            /> */}
            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={savingTransactionIds || (!showInternalTransactionId && !externalTransactionId) || (showInternalTransactionId && !internalTransactionId && !externalTransactionId)}
              >
                {savingTransactionIds ? 'Saving...' : 'Save'}
              </Button>
            </Box> */}
            {transactionIdMessage && (
              <Alert severity="success">{transactionIdMessage}</Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}